import { Alert, Backdrop, Box, Card, Divider, FormControl, FormControlLabel, Grid, IconButton, MenuItem, Radio, RadioGroup, Select, Snackbar, Switch, Tooltip, Typography } from '@mui/material';
import { useStyles } from './styles';
import { useHistory, useParams } from 'react-router';
import whiteBackIcon from '../../icons/whiteBackIcon.svg';
import { useDispatch, useSelector } from 'react-redux';
import { getUpdatePropertyGroupConfigs, selectPropertyGroupDetailsState, selectSpecificPropertyGroupConfigs } from '../../shared/sagas/propertyGroups/propertyGroupsSlice';
import { useCallback, useEffect, useState } from 'react';
import { PmConfigType } from './pmConfigType';
import { selectCurrentUser } from '../../shared/sagas/auth/authSlice';
import debounce from 'lodash.debounce';
import { getPmDetails } from '../onboarding/sagas/pmonboardingSlice';
import DateConfig from './components/DateConfig';
import { Role, WorkOrderCostApprovalEnum } from '@inosio/payments-sdk';
import HelpIcon from '@mui/icons-material/Help';
import { getAvailableBankAccount, selectBillingState } from '../billingConfiguration/sagas/billingSlice';
import AmountInputConfig from './components/AmountInputConfig';
import { ErrorObj } from './components/ApplicationsDashboard';

export function OwnerPortalConfigSettings() {
  //variables
  const { uuid: propertyGroupIdParam } = useParams<{ uuid: string }>();
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useDispatch();
  const todayDate = new Date();
  let tomorrowDate = new Date();
  tomorrowDate.setDate(todayDate.getDate() + 1);

  //selectors
  const { selectedPropertyGroupAssignedConfigs, isFetchingAllPropertyGroupConfigsSuccess, isUpdatingPropertyGroupConfigsSuccess, UpdatingPropertyGroupConfigsError } =
    useSelector(selectPropertyGroupDetailsState);
  const currentUser = useSelector(selectCurrentUser);
  const { availableBankAccounts } = useSelector(selectBillingState);

  //local states
  const [pmConfig, setPmConfig] = useState<PmConfigType>({});
  const [pendingConfig, setPendingConfig] = useState<any>(null);
  const [snackBarOpen, setSnackBarOpen] = useState(false);
  const [snackBarErrorMessage, setSnackBarErrorMessage] = useState('');
  const [costApproval, setCostApproval] = useState<WorkOrderCostApprovalEnum | null | undefined>(null);

  //useffects
  useEffect(() => {
    setPmConfig(selectedPropertyGroupAssignedConfigs);
    if (selectedPropertyGroupAssignedConfigs.WorkOrderCostApproval) setCostApproval(selectedPropertyGroupAssignedConfigs.WorkOrderCostApproval as WorkOrderCostApprovalEnum);
  }, [selectedPropertyGroupAssignedConfigs]);

  useEffect(() => {
    dispatch({
      type: selectSpecificPropertyGroupConfigs.type,
      payload: {
        propertyGroupUuid: propertyGroupIdParam,
      },
    });
  }, [isFetchingAllPropertyGroupConfigsSuccess]);

  //on load fetch all bank accounts user has access to
  useEffect(() => {
    dispatch({ type: getAvailableBankAccount.type, payload: { fundableOnly: true } });
  }, []);

  //error handling
  useEffect(() => {
    if (UpdatingPropertyGroupConfigsError.message?.length > 0) {
      setSnackBarOpen(true);
      setSnackBarErrorMessage(UpdatingPropertyGroupConfigsError.message);
    } else {
      setSnackBarErrorMessage('');
    }
  }, [UpdatingPropertyGroupConfigsError]);

  const [errorObj, setErrorObj] = useState<ErrorObj>({});
  const handleError = (key: string, value: string) => setErrorObj({ ...errorObj, [key]: value });

  //handlers
  const handleChange = (ConfigTypeKey1: string, AssignedValue1: any, ConfigTypeKey2?: string, AssignedValue2?: any) => {
    let newPmConfig = {};

    if (ConfigTypeKey2 && AssignedValue2 !== undefined) {
      newPmConfig = {
        ...pmConfig,
        [ConfigTypeKey1]: AssignedValue1,
        [ConfigTypeKey2]: AssignedValue2,
      };
      setPendingConfig({
        ...pendingConfig,
        [ConfigTypeKey1]: AssignedValue1,
        [ConfigTypeKey2]: AssignedValue2,
      });
    } else {
      newPmConfig = {
        ...pmConfig,
        [ConfigTypeKey1]: AssignedValue1,
      };
      setPendingConfig({
        ...pendingConfig,
        [ConfigTypeKey1]: AssignedValue1,
      });
    }

    setPmConfig(newPmConfig);
  };

  const actuallyDispatchConfigUpdate = () => {
    if (pendingConfig) {
      dispatch(getUpdatePropertyGroupConfigs({ propertyGroupUuid: propertyGroupIdParam, productTypeId: 5, updatedPmConfig: pendingConfig }));
      setPendingConfig(null);
    }
  };

  const delayedConfigUpdate = useCallback(debounce(actuallyDispatchConfigUpdate, 750), [pendingConfig]);

  const handleAlertClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }

    setSnackBarOpen(false);
  };

  useEffect(() => {
    delayedConfigUpdate();
    return delayedConfigUpdate.cancel;
  }, [pendingConfig, delayedConfigUpdate]);

  //fetching pm details on successful update bcz it has the features enabled data which can be impacted by this config update
  useEffect(() => {
    if (isUpdatingPropertyGroupConfigsSuccess) {
      dispatch(getPmDetails());
    }
  }, [isUpdatingPropertyGroupConfigsSuccess]);

  const getTypography = (text: string, fontWeight: number, fontSize: string, restProps?: object) => (
    <Typography
      sx={{
        fontFamily: 'Work Sans',
        fontWeight: { fontWeight },
        fontSize: { fontSize },
        lineHeight: '22px',
        ...restProps,
      }}
    >
      {text}
    </Typography>
  );

  return (
    <Box mt={4}>
      <Card sx={{ display: 'flex', flexDirection: 'column', color: '#000' }}>
        <Box className={classes.headerContainer} sx={{ backgroundColor: '#26ab7f' }}>
          <IconButton
            sx={{ marginRight: '10px', marginTop: '-4px' }}
            onClick={() => {
              history.push(`/pmui/propertygroups/${propertyGroupIdParam}`);
            }}
          >
            <img src={whiteBackIcon} alt="" height="14px" />
          </IconButton>
          {pmConfig.OwnerPortalProductPurchased && pmConfig.OwnerPortalMasterFlag && (
            <FormControlLabel
              sx={{ color: '#FFF', ml: 0 }}
              control={
                <Switch
                  checked={!!pmConfig.OwnerPortalProductEnabled}
                  onChange={(event) => handleChange(event.target.name, event.target.checked)}
                  name="OwnerPortalProductEnabled"
                  disabled={currentUser?.currentProfile.role === Role.PropertyManager}
                />
              }
              label={getTypography(selectedPropertyGroupAssignedConfigs.Name + ': Owner Portal', 600, '24px')}
              labelPlacement="start"
            />
          )}
        </Box>

        <Grid container sx={{ p: 2, pt: 2, pb: 1, position: 'relative' }}>
          <Grid item sm={12} md={6}>
            <Box className={classes.configContainer}>
              <Box className={classes.configHeaderContainer}>{getTypography('Invitation Emails', 600, '18px')}</Box>
              <Box className={classes.configBodyContainer}>
                <DateConfig
                  name="OwnerPortalGoLiveDate"
                  label={'Go live date:'}
                  description="The date when all current owners are invited to use Inosio."
                  onChange={handleChange}
                  dateConfigValue={selectedPropertyGroupAssignedConfigs.OwnerPortalGoLiveDate}
                  minDate={tomorrowDate}
                />
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box className={classes.configContainer}>
              <Box className={classes.configHeaderContainer}>
                {getTypography('Payment Details', 600, '18px')}
                <Tooltip title="Which bank account should we deposit owner payments to ?" placement="right">
                  <HelpIcon sx={{ fontSize: 16, color: '#C4C4C4', cursor: 'pointer', ml: '10px' }} />
                </Tooltip>
              </Box>
              <Box className={classes.configBodyContainer}>
                <Divider style={{ border: '0.5px solid #F1F0F5' }} sx={{ mx: -2 }} />
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1 }}>
                  {getTypography('Owner Payments Deposit Account:', 400, '18px', { p: 1.5 })}
                  {!!availableBankAccounts?.length && (
                    <Select
                      value={pmConfig.OwnerPaymentDestinationBankAccountUuid ?? ''}
                      displayEmpty
                      sx={{ borderRadius: '24px', height: '31px', '.MuiTypography-root': { fontSize: '14px' } }}
                      onChange={(e) => handleChange('OwnerPaymentDestinationBankAccountUuid', e.target.value)}
                    >
                      <MenuItem disabled value={''}>
                        <Typography>Select account</Typography>
                      </MenuItem>
                      {availableBankAccounts.map((bankAccount) => (
                        <MenuItem value={bankAccount.bankAccountUuid} key={bankAccount.bankAccountUuid} selected={bankAccount.bankAccountUuid === pmConfig.OwnerPaymentDestinationBankAccountUuid}>
                          <Typography>****{bankAccount.last4}</Typography>
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                </Box>
              </Box>
            </Box>
          </Grid>
          <Grid item sm={12} md={6}>
            <Box className={classes.configContainer}>
              <Box className={classes.configHeaderContainer}>
                {getTypography('Default Cost Limit', 600, '18px')}
                <Tooltip title="Your organization's default maximum maintenance expense before owners wish to be contacted for prior approval." placement="right">
                  <HelpIcon sx={{ fontSize: 16, color: '#C4C4C4', cursor: 'pointer', ml: '10px' }} />
                </Tooltip>
              </Box>
              <Box className={classes.configBodyContainer}>
                <Box sx={{ display: 'flex', alignItems: 'center', py: 1 }}>
                  {getTypography('Cost Limit?', 400, '18px', { p: 2 })}
                  <Box sx={{ width: 100, mx: 2, my: 0.7 }}>
                    <AmountInputConfig name="WorkOrderDefaultCostLimit" value={pmConfig.WorkOrderDefaultCostLimit} onChange={handleChange} error={''} onError={handleError} />
                  </Box>
                </Box>
                {errorObj.WorkOrderDefaultCostLimit && (
                  <Typography
                    sx={{
                      mt: '-24px',
                      mb: 1,
                      mr: 2,
                      textAlign: 'right',
                    }}
                    className={classes.errorText}
                  >
                    {errorObj.WorkOrderDefaultCostLimit}
                  </Typography>
                )}
                <Box sx={{ p: 2 }}>
                  {getTypography('Call for approval:', 400, '18px', { mb: 2 })}
                  <FormControl component="fieldset">
                    <RadioGroup
                      value={costApproval ?? ''}
                      onChange={(event) => {
                        setCostApproval(event.target.value as WorkOrderCostApprovalEnum);
                        handleChange('WorkOrderCostApproval', event.target.value);
                      }}
                    >
                      <FormControlLabel
                        value={WorkOrderCostApprovalEnum.AboveLimit}
                        control={<Radio />}
                        label={<Typography sx={{ color: '#14142B', fontFamily: 'Work Sans', fontSize: '16px', fontWeight: 500 }}>Above this limit</Typography>}
                      />
                      <FormControlLabel
                        value={WorkOrderCostApprovalEnum.Always}
                        control={<Radio />}
                        label={<Typography sx={{ color: '#14142B', fontFamily: 'Work Sans', fontSize: '16px', fontWeight: 500 }}>Always Call</Typography>}
                      />
                      <FormControlLabel
                        value={WorkOrderCostApprovalEnum.Never}
                        control={<Radio />}
                        label={<Typography sx={{ color: '#14142B', fontFamily: 'Work Sans', fontSize: '16px', fontWeight: 500 }}>Never Call</Typography>}
                      />
                    </RadioGroup>
                  </FormControl>
                </Box>
              </Box>
            </Box>
          </Grid>
          <Backdrop
            sx={{ color: '#fff', zIndex: 30, position: 'absolute', backgroundColor: 'rgb(255, 255, 255, 0.5)' }}
            open={!pmConfig.OwnerPortalProductEnabled || !pmConfig.OwnerPortalProductPurchased || !pmConfig.OwnerPortalMasterFlag}
          ></Backdrop>
        </Grid>
      </Card>
      <Snackbar anchorOrigin={{ horizontal: 'center', vertical: 'top' }} open={snackBarOpen} autoHideDuration={4000} onClose={handleAlertClose}>
        <Alert onClose={handleAlertClose} severity="error">
          {snackBarErrorMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}
